import React, { useState, useEffect } from "react";
import instance from "../../instance";
import "./LoginRegister.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTimes,
  FaCheck,
  FaTimesCircle,
  FaCopy,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function UserList() {
  const [users, setUsers] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showQRCode, setShowQRCode] = useState(null);
  const [current2FAUser, setCurrent2FAUser] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to view users.");
      return;
    }

    try {
      const response = await instance.get(`users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users. Please try again later.");
    }
  };

  const handleEdit = (userId, field, value) => {
    setEditingField({ userId, field });
    setEditedValue(value);
  };

  const handleSave = async (userId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to save changes.");
      return;
    }

    const updatedUser = {
      [editingField.field]: editedValue,
    };

    try {
      await instance.put(`users/${userId}`, updatedUser, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("User updated successfully");
      setEditingField(null);
      fetchUsers();
    } catch (error) {
      console.error(
        "Failed to update user:",
        error.response?.data || error.message
      );
      toast.error(
        `Failed to update user: ${
          error.response?.data?.message || "Please try again later."
        }`
      );
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditedValue("");
  };

  const handleInputChange = (e) => {
    setEditedValue(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleVerificationCodeChange = (e) => {
    const input = e.target.value.replace(/\D/g, "");
    setVerificationCode(input.slice(0, 6));
  };

  const verify2FA = async (userId) => {
    setIsVerifying(true);
    try {
      const response = await instance.post(`users/verify2fa/${userId}`, {
        code: verificationCode,
      });

      if (response.data.success) {
        toast.success("2FA successfully activated.");
        setShowQRCode(null);
        setCurrent2FAUser(null);
        setIsModalOpen(false);
        fetchUsers();
      } else {
        toast.error("Invalid verification code. Please try again.");
      }
    } catch (error) {
      console.error(
        "Failed to verify 2FA:",
        error.response?.data || error.message
      );
      toast.error(
        `Failed to verify 2FA: ${
          error.response?.data?.message || "Please try again later."
        }`
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus, username) => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to update user status.");
      return;
    }

    const newStatus = !currentStatus;

    try {
      const response = await instance.put(
        `users/enable2fa/${userId}`,
        { username: username }, // أرسل username كـ JSON
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (newStatus) {
        setShowQRCode(response.data.qrCodeUrl);
        setSecretKey(response.data.secret);
        setCurrent2FAUser(userId);
        setIsModalOpen(true);
      } else {
        setShowQRCode(null);
        setCurrent2FAUser(null);
        setSecretKey("");
      }

      toast.success("User status updated successfully");
      fetchUsers();
    } catch (error) {
      console.error(
        "Failed to update user status:",
        error.response?.data || error.message
      );
      toast.error(
        `Failed to update user status: ${
          error.response?.data?.message || "Please try again later."
        }`
      );
    }
  };

  const copyToClipboard = () => {
    const textArea = document.createElement("textarea");
    textArea.value = secretKey;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const msg = successful ? "successful" : "unsuccessful";
      console.log("Fallback: Copying text command was " + msg);
      toast.success("Secret key copied to clipboard");
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
      toast.error(
        "Failed to copy secret key. Please try manually selecting and copying the text."
      );
    }

    document.body.removeChild(textArea);
  };
  const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          {children}
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <button onClick={() => navigate("/")} className="button">
        ← Main
      </button>
      <button
        onClick={() => (window.location.href = "/logs")}
        className="button"
      >
        User Logs
      </button>
      <div className="container">
        <header>
          <h2>Users</h2>
        </header>

        <div className="user-list">
          {users.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>User name</th>
                  <th>Password</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.UserID}>
                    <td>
                      {editingField?.userId === user.UserID &&
                      editingField?.field === "Username" ? (
                        <input
                          type="text"
                          value={editedValue}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <>
                          {user.Username}
                          <button
                            className="icon-button"
                            onClick={() =>
                              handleEdit(user.UserID, "Username", user.Username)
                            }
                          >
                            <FaEdit />
                          </button>
                        </>
                      )}
                    </td>
                    <td>
                      {editingField?.userId === user.UserID &&
                      editingField?.field === "Password" ? (
                        <div className="password-input-container">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={editedValue}
                            onChange={handleInputChange}
                            placeholder="Enter new password"
                          />
                          <button
                            className="icon-button"
                            onClick={togglePasswordVisibility}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      ) : (
                        <>
                          ••••••••
                          <button
                            className="icon-button"
                            onClick={() =>
                              handleEdit(user.UserID, "Password", "")
                            }
                          >
                            <FaEdit />
                          </button>
                        </>
                      )}
                    </td>
                    <td>
                      {editingField?.userId === user.UserID &&
                      editingField?.field === "RoleID" ? (
                        <select
                          value={editedValue}
                          onChange={handleInputChange}
                        >
                          <option value="1">Admin</option>
                          <option value="2">User</option>
                          <option value="4">Supervisor</option>
                        </select>
                      ) : (
                        <>
                          {user.RoleName}
                          <button
                            className="icon-button"
                            onClick={() =>
                              handleEdit(user.UserID, "RoleID", user.RoleID)
                            }
                          >
                            <FaEdit />
                          </button>
                        </>
                      )}
                    </td>
                    <td>
                      <button
                        className={`status-toggle ${
                          user.IsEnabled ? "enabled" : "disabled"
                        }`}
                        onClick={() =>
                          toggleUserStatus(
                            user.UserID,
                            user.IsEnabled,
                            user.Username
                          )
                        }
                      >
                        {user.IsEnabled ? <FaCheck /> : <FaTimesCircle />}
                      </button>
                    </td>
                    <td>
                      {editingField?.userId === user.UserID ? (
                        <>
                          <button
                            className="save-button"
                            onClick={() => handleSave(user.UserID)}
                          >
                            <FaSave /> Save
                          </button>
                          <button
                            className="cancel-button"
                            onClick={handleCancel}
                          >
                            <FaTimes /> Cancel
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No users available.</p>
          )}
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {current2FAUser && showQRCode && (
          <div className="verification-modal">
            <header>Two Factor Verification</header>
            <img src={showQRCode} alt="QR Code" />
            <div className="secret-container">
              <p>
                Secret Key: <strong id="secretKeyText">{secretKey}</strong>
              </p>
              <button className="icon-button" onClick={copyToClipboard}>
                <FaCopy /> Copy <strong>Secret Key</strong>
              </button>
            </div>
            <div className="verification-container">
              <input
                type="text"
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                maxLength={6}
                autoFocus
              />
              <button
                className="verify-button"
                onClick={() => verify2FA(current2FAUser)}
                disabled={isVerifying}
              >
                Verify
              </button>
              <button
                className="cancel-button"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default UserList;
